import React, { useEffect, useMemo, useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import spformsApi from '../lib/spformsApi';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import EmailMessageModal from '../components/EmailMessageModal';
import { formatDateEU, formatDateTimeEU } from '../utils/dateFormat';

const {
  FiRefreshCw,
  FiCheckCircle,
  FiUsers,
  FiCreditCard,
  FiX,
  FiSave,
  FiDownload,
  FiFileText,
  FiClock,
  FiAlertCircle,
  FiEye,
  FiMail,
  FiSend,
} = FiIcons;

const PAYMENT_STATUSES = [
  { value: 'pending', label: 'Pending' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'partial', label: 'Partial' },
  { value: 'paid', label: 'Paid' },
];

const PAYMENT_GATEWAYS = [
  { value: '', label: 'Select Method' },
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'wire', label: 'Bank Wire' },
];

function formatMoney(value) {
  const n = Number(value || 0);
  return `€${n.toFixed(2)}`;
}

function resolveAmountDue(row, selectedGroup = null) {
  const amountDue = Number(row?.amount_due || 0);
  const finalPrice = Number(row?.final_price || row?.final_price_snapshot || 0);
  const groupPrice = Number(row?.price_amount || selectedGroup?.price_amount || 0);

  if (amountDue > 0) return amountDue;
  if (finalPrice > 0) return finalPrice;
  if (groupPrice > 0) return groupPrice;

  return 0;
}

function normalizeRows(rows) {
  return Array.isArray(rows) ? rows : [];
}

function escapeCsvCell(value) {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function downloadCsv(filename, rows) {
  const safeRows = normalizeRows(rows);
  if (safeRows.length === 0) {
    alert('No rows to export');
    return;
  }
  const headers = Object.keys(safeRows[0]);
  const csv = [
    headers.join(','),
    ...safeRows.map((row) => headers.map((header) => escapeCsvCell(row[header])).join(',')),
  ].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadExcel(filename, rows) {
  const safeRows = normalizeRows(rows);
  if (safeRows.length === 0) {
    alert('No rows to export');
    return;
  }
  const headers = Object.keys(safeRows[0]);
  const html = `
    <html>
      <head>
        <meta charset="UTF-8" />
      </head>
      <body>
        <table border="1">
          <thead>
            <tr>
              ${headers.map((header) => `<th>${String(header)}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
            ${safeRows
              .map(
                (row) => `
              <tr>
                ${headers
                  .map((header) => {
                    const value = row[header] === null || row[header] === undefined ? '' : row[header];
                    return `<td>${String(value).replace(/</g, '&lt;').replace(/>/g, '&gt;')}</td>`;
                  })
                  .join('')}
              </tr>
            `
              )
              .join('')}
          </tbody>
        </table>
      </body>
    </html>
  `;

  const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function statusBadgeClasses(status) {
  switch (status) {
    case 'approved':
    case 'paid':
      return 'bg-emerald-50 text-emerald-700 border-emerald-100';
    case 'partial':
    case 'pending':
      return 'bg-amber-50 text-amber-700 border-amber-100';
    case 'rejected':
    case 'unpaid':
      return 'bg-red-50 text-red-700 border-red-100';
    default:
      return 'bg-gray-50 text-gray-600 border-gray-100';
  }
}

function pickRegistrationId(row) {
  return row.registration_id || row.id;
}

function pickParticipantId(row) {
  return row.participant_id || row.id;
}

function buildCoreFromRow(row) {
  return {
    first_name: row.first_name || '',
    last_name: row.last_name || '',
    full_name: `${row.first_name || ''} ${row.last_name || ''}`.trim(),
    email: row.email || '',
    phone: row.phone || '',
    parent_name: row.parent_name || '',
    parent_mobile: row.parent_mobile || row.phone || '',
    date_of_birth: row.date_of_birth || '',
    birth_year: row.birth_year || null,
    gender: row.gender || '',
    current_team: row.current_team || '',
    current_club: row.current_club || '',
    jersey_size: row.jersey_size || '',
  };
}

export default function Participants() {
  const { tenantId } = useTenant();
  const { session } = useAuth();

  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [activeTab, setActiveTab] = useState('registrations');

  const [registrations, setRegistrations] = useState([]);
  const [participants, setParticipants] = useState([]);

  const [loadingGroups, setLoadingGroups] = useState(true);
  const [loadingRegistrations, setLoadingRegistrations] = useState(false);
  const [loadingParticipants, setLoadingParticipants] = useState(false);

  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [editingPayment, setEditingPayment] = useState(null);
  const [viewingRegistration, setViewingRegistration] = useState(null);
  const [emailBusyKey, setEmailBusyKey] = useState('');
  const [emailMessageModal, setEmailMessageModal] = useState({
    open: false,
    mode: 'single',
    participant: null,
    participants: [],
  });

  const selectedGroup = useMemo(() => {
    return groups.find((group) => group.id === selectedGroupId) || null;
  }, [groups, selectedGroupId]);

  const modalTenant = useMemo(() => {
    return {
      id: tenantId,
      name:
        session?.tenant_name ||
        session?.tenant?.name ||
        session?.company_name ||
        selectedGroup?.tenant_name ||
        '',
      contact_email:
        session?.tenant_email ||
        session?.tenant?.contact_email ||
        session?.contact_email ||
        session?.email ||
        '',
    };
  }, [tenantId, session, selectedGroup]);

  const loadGroups = async () => {
  if (!tenantId) {
    setGroups([]);
    setSelectedGroupId('');
    setLoadingGroups(false);
    return;
  }

  try {
    setLoadingGroups(true);
    setErrorMessage('');

    const data = await spformsApi.intakeGroupsList(tenantId);
    setGroups(data);

    const selectedStillExists = data.some((group) => group.id === selectedGroupId);

    if (!selectedGroupId || !selectedStillExists) {
      setSelectedGroupId(data[0]?.id || '');
    }
  } catch (error) {
    setErrorMessage(error.message || 'Failed to load events');
  } finally {
    setLoadingGroups(false);
  }
};

  const loadRegistrations = async () => {
    if (!tenantId || !selectedGroupId) {
      setRegistrations([]);
      return;
    }

    try {
      setLoadingRegistrations(true);
      setErrorMessage('');
      const data = await spformsApi.registrationsList(tenantId, selectedGroupId);
      setRegistrations(data);
    } catch (error) {
      setErrorMessage(error.message || 'Failed to load registrations');
    } finally {
      setLoadingRegistrations(false);
    }
  };

  const loadParticipants = async () => {
    if (!tenantId || !selectedGroupId) {
      setParticipants([]);
      return;
    }

    try {
      setLoadingParticipants(true);
      setErrorMessage('');
      const data = await spformsApi.participantsList(tenantId, selectedGroupId);
      setParticipants(data);
    } catch (error) {
      setErrorMessage(error.message || 'Failed to load participants');
    } finally {
      setLoadingParticipants(false);
    }
  };

  const loadAll = async () => {
    await Promise.all([loadRegistrations(), loadParticipants()]);
  };

  useEffect(() => {
    loadGroups();
  }, [tenantId]);

useEffect(() => {
  if (tenantId && selectedGroupId) {
    loadAll();
  } else {
    setRegistrations([]);
    setParticipants([]);
  }
}, [tenantId, selectedGroupId]);

  const sendEmailToRow = async (row, emailType = 'confirmation') => {
    if (!selectedGroup) {
      setErrorMessage('Selected event not found');
      return false;
    }

    const rowId = pickRegistrationId(row) || pickParticipantId(row) || row.email || emailType;
    const busyKey = `${emailType}-${rowId}`;

    try {
      setEmailBusyKey(busyKey);
      setErrorMessage('');
      setSuccessMessage('');

      await spformsApi.sendRegistrationConfirmationEmail({
        email_type: emailType,
        public_slug: selectedGroup.public_slug || '',
        registration_id: pickRegistrationId(row) || row.registration_id || null,
        participant_id: pickParticipantId(row) || row.participant_id || null,
        core: buildCoreFromRow(row),
        answers: row.answers || row.answers_json || {},
        discount_code: row.discount_code || '',
        final_price:
          row.final_price ??
          row.final_price_snapshot ??
          row.amount_due ??
          selectedGroup.price_amount ??
          0,
        amount_due: row.amount_due ?? row.final_price ?? row.final_price_snapshot ?? selectedGroup.price_amount ?? 0,
        amount_paid: row.amount_paid ?? 0,
        new_payment_amount: row.new_payment_amount ?? 0,
        remaining_balance: row.remaining_balance ?? 0,
        payment_status: row.payment_status || '',
        payment_gateway: row.payment_gateway || '',
        group: selectedGroup,
      });

      if (emailType === 'approval') {
        setSuccessMessage('Approval email sent successfully');
      } else if (emailType === 'payment') {
        setSuccessMessage('Payment email sent successfully');
      } else {
        setSuccessMessage('Confirmation email sent successfully');
      }

      return true;
    } catch (error) {
      setErrorMessage(error.message || 'Failed to send email');
      return false;
    } finally {
      setEmailBusyKey('');
    }
  };

  const handleRegistrationStatus = async (registration, status) => {
    if (!tenantId) return;

    const registrationId = pickRegistrationId(registration);
    if (!registrationId) {
      setErrorMessage('Registration ID not found');
      return;
    }

    try {
      setErrorMessage('');
      setSuccessMessage('');

      if (status === 'approved') {
        await spformsApi.participantApprove(registrationId, tenantId);
        setSuccessMessage('Registration approved and participant created');

        try {
          await sendEmailToRow(registration, 'approval');
        } catch {
          // sendEmailToRow already handles visible error
        }
      } else {
        await spformsApi.registrationUpdateStatus(registrationId, status, tenantId);
        setSuccessMessage(`Registration marked as ${status}`);
      }

      await loadAll();
    } catch (error) {
      setErrorMessage(error.message || 'Failed to update registration');
    }
  };

  const openPaymentModal = (participant) => {
    const resolvedAmountDue = resolveAmountDue(participant, selectedGroup);
    const alreadyPaid = Number(participant.amount_paid || 0);
    const remainingAmount = Math.max(resolvedAmountDue - alreadyPaid, 0);

    setEditingPayment({
      ...participant,
      participant_id: pickParticipantId(participant),
      payment_status: participant.payment_status || 'pending',
      amount_due: resolvedAmountDue,
      amount_paid: alreadyPaid,
      remaining_amount: remainingAmount,
      new_payment_amount: '',
      payment_gateway: participant.payment_gateway || '',
    });
  };

  const updatePayment = async (e) => {
    e.preventDefault();
    if (!editingPayment || !tenantId) return;

    try {
      setErrorMessage('');
      setSuccessMessage('');

      const amountDue = Number(editingPayment.amount_due || 0);
      const alreadyPaid = Number(editingPayment.amount_paid || 0);
      const newPayment = Number(editingPayment.new_payment_amount || 0);
      const totalPaid = alreadyPaid + newPayment;
      const remainingBalance = Math.max(amountDue - totalPaid, 0);

      const nextPaymentStatus =
        totalPaid >= amountDue
          ? 'paid'
          : totalPaid > 0
          ? 'partial'
          : 'pending';

      await spformsApi.participantPaymentUpdate({
        tenant_id: tenantId,
        participant_id: editingPayment.participant_id,
        payment_status: nextPaymentStatus,
        amount_due: amountDue,
        amount_paid: totalPaid,
        payment_gateway: editingPayment.payment_gateway,
      });

      const shouldSendPaymentEmail = totalPaid > 0;

      if (shouldSendPaymentEmail) {
        try {
          await sendEmailToRow(
            {
              ...editingPayment,
              amount_due: amountDue,
              amount_paid: totalPaid,
              new_payment_amount: newPayment,
              remaining_balance: remainingBalance,
              payment_status: nextPaymentStatus,
            },
            'payment'
          );
        } catch {
          // sendEmailToRow already handles visible error
        }
      }

      setEditingPayment(null);
      setSuccessMessage(
        shouldSendPaymentEmail
          ? 'Payment updated successfully and email sent'
          : 'Payment updated successfully'
      );

      await loadParticipants();
    } catch (error) {
      setErrorMessage(error.message || 'Failed to update payment');
    }
  };

  const sendBulkEmail = async (rows, emailType) => {
    const safeRows = normalizeRows(rows).filter((row) => row.email);

    if (safeRows.length === 0) {
      setErrorMessage('No email addresses found for bulk send');
      return;
    }

    const confirmMessage =
      emailType === 'approval'
        ? `Send approval email to ${safeRows.length} participant(s)?`
        : emailType === 'payment'
        ? `Send payment email to ${safeRows.length} participant(s)?`
        : `Resend confirmation email to ${safeRows.length} registration(s)?`;

    if (!window.confirm(confirmMessage)) return;

    let sent = 0;
    let failed = 0;

    setErrorMessage('');
    setSuccessMessage('');
    setEmailBusyKey(`bulk-${emailType}`);

    try {
      for (const row of safeRows) {
        try {
          await spformsApi.sendRegistrationConfirmationEmail({
            email_type: emailType,
            public_slug: selectedGroup?.public_slug || '',
            registration_id: pickRegistrationId(row) || null,
            participant_id: pickParticipantId(row) || null,
            core: buildCoreFromRow(row),
            answers: row.answers || row.answers_json || {},
            discount_code: row.discount_code || '',
            final_price:
              row.final_price ??
              row.final_price_snapshot ??
              row.amount_due ??
              selectedGroup?.price_amount ??
              0,
            amount_due: row.amount_due ?? row.final_price ?? row.final_price_snapshot ?? selectedGroup?.price_amount ?? 0,
            amount_paid: row.amount_paid ?? 0,
            new_payment_amount: row.new_payment_amount ?? 0,
            remaining_balance: row.remaining_balance ?? 0,
            payment_status: row.payment_status || '',
            payment_gateway: row.payment_gateway || '',
            group: selectedGroup || {},
          });
          sent += 1;
        } catch (error) {
          failed += 1;
          console.error('Bulk email failed for row:', row, error);
        }
      }

      setSuccessMessage(`Bulk email completed. Sent: ${sent}. Failed: ${failed}.`);
    } finally {
      setEmailBusyKey('');
    }
  };

  const openCustomEmailModal = (row) => {
    if (!row) return;

    setErrorMessage('');
    setSuccessMessage('');
    setEmailMessageModal({
      open: true,
      mode: 'single',
      participant: {
        ...row,
        participant_id: pickParticipantId(row) || row.participant_id || null,
        registration_id: pickRegistrationId(row) || row.registration_id || null,
      },
      participants: [],
    });
  };

  const openBulkCustomEmailModal = (rows) => {
    const safeRows = normalizeRows(rows).filter((row) => row.email);

    if (safeRows.length === 0) {
      setErrorMessage('No email addresses found for custom bulk message');
      return;
    }

    setErrorMessage('');
    setSuccessMessage('');
    setEmailMessageModal({
      open: true,
      mode: 'bulk',
      participant: null,
      participants: safeRows.map((row) => ({
        ...row,
        participant_id: pickParticipantId(row) || row.participant_id || null,
        registration_id: pickRegistrationId(row) || row.registration_id || null,
      })),
    });
  };

  const registrationExportRows = useMemo(() => {
    return registrations.map((row) => ({
      registration_id: pickRegistrationId(row),
      status: row.status || row.registration_status || 'pending',
      first_name: row.first_name || '',
      last_name: row.last_name || '',
      date_of_birth: row.date_of_birth || '',
      birth_year: row.birth_year || '',
      gender: row.gender || '',
      parent_name: row.parent_name || '',
      parent_mobile: row.parent_mobile || row.phone || '',
      email: row.email || '',
      phone: row.phone || '',
      current_team: row.current_team || '',
      current_club: row.current_club || '',
      jersey_size: row.jersey_size || '',
      discount_code: row.discount_code || '',
      final_price: row.final_price || '',
      submitted_at: row.created_at || row.submitted_at || '',
    }));
  }, [registrations]);

  const participantExportRows = useMemo(() => {
    return participants.map((row) => ({
      participant_id: pickParticipantId(row),
      first_name: row.first_name || '',
      last_name: row.last_name || '',
      date_of_birth: row.date_of_birth || '',
      birth_year: row.birth_year || '',
      gender: row.gender || '',
      parent_name: row.parent_name || '',
      parent_mobile: row.parent_mobile || row.phone || '',
      email: row.email || '',
      phone: row.phone || '',
      current_team: row.current_team || '',
      current_club: row.current_club || '',
      jersey_size: row.jersey_size || '',
      payment_status: row.payment_status || '',
      amount_due: row.amount_due ?? '',
      amount_paid: row.amount_paid ?? '',
      payment_gateway: row.payment_gateway || '',
      approved_at: row.approved_at || row.created_at || '',
    }));
  }, [participants]);

  const exportBaseName = selectedGroup
    ? selectedGroup.title.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase()
    : 'sportikoforms';

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-4xl font-black text-gray-900 tracking-tighter">
            Participants
          </h1>
          <p className="text-gray-500 font-medium tracking-tight">
            Review registrations, approve players, export data, and track payments.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <select
            value={selectedGroupId}
            onChange={(e) => setSelectedGroupId(e.target.value)}
            disabled={loadingGroups}
            className="px-6 py-4 bg-white border-2 border-gray-100 rounded-[1.5rem] font-black text-sm outline-none focus:border-indigo-500 shadow-sm"
          >
            {groups.length === 0 ? (
              <option value="">No events found</option>
            ) : (
              groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.title}
                </option>
              ))
            )}
          </select>
          <button
            type="button"
            onClick={loadAll}
            className="w-14 h-14 bg-white border-2 border-gray-100 rounded-[1.5rem] flex items-center justify-center hover:bg-gray-50 transition-colors shadow-sm"
          >
            <SafeIcon icon={FiRefreshCw} />
          </button>
        </div>
      </div>

      {errorMessage ? (
        <div className="rounded-2xl border border-red-100 bg-red-50 px-5 py-4 text-sm font-bold text-red-700 flex items-center gap-3">
          <SafeIcon icon={FiAlertCircle} />
          {errorMessage}
        </div>
      ) : null}

      {successMessage ? (
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-5 py-4 text-sm font-bold text-emerald-700 flex items-center gap-3">
          <SafeIcon icon={FiCheckCircle} />
          {successMessage}
        </div>
      ) : null}

      <div className="bg-white border rounded-[2rem] p-2 shadow-sm flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          onClick={() => setActiveTab('registrations')}
          className={`flex-1 rounded-[1.5rem] px-6 py-4 text-sm font-black uppercase tracking-widest transition-all flex items-center justify-center gap-3 ${
            activeTab === 'registrations'
              ? 'bg-indigo-600 text-white shadow-xl shadow-indigo-100'
              : 'text-gray-500 hover:bg-gray-50'
          }`}
        >
          <SafeIcon icon={FiClock} />
          Registrations
          <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px]">
            {registrations.length}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('approved')}
          className={`flex-1 rounded-[1.5rem] px-6 py-4 text-sm font-black uppercase tracking-widest transition-all flex items-center justify-center gap-3 ${
            activeTab === 'approved'
              ? 'bg-emerald-600 text-white shadow-xl shadow-emerald-100'
              : 'text-gray-500 hover:bg-gray-50'
          }`}
        >
          <SafeIcon icon={FiUsers} />
          Approved
          <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px]">
            {participants.length}
          </span>
        </button>
      </div>

      {activeTab === 'registrations' ? (
        <div className="bg-white border rounded-[2.5rem] shadow-sm overflow-hidden">
          <div className="p-8 border-b bg-gray-50/50 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-xl font-black text-gray-900 tracking-tight">
                Registrations / Applications
              </h2>
              <p className="text-xs text-gray-500 font-bold uppercase tracking-widest mt-1">
                Submitted forms waiting for review
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => sendBulkEmail(registrations, 'confirmation')}
                disabled={emailBusyKey === 'bulk-confirmation'}
                className="export-button"
              >
                <SafeIcon icon={FiMail} />
                {emailBusyKey === 'bulk-confirmation' ? 'Sending...' : 'Email All'}
              </button>
              <button
                type="button"
                onClick={() => openBulkCustomEmailModal(registrations)}
                className="export-button"
              >
                <SafeIcon icon={FiSend} />
                Custom Message
              </button>
              <button
                type="button"
                onClick={() => downloadCsv(`${exportBaseName}-registrations.csv`, registrationExportRows)}
                className="export-button"
              >
                <SafeIcon icon={FiDownload} /> CSV
              </button>
              <button
                type="button"
                onClick={() => downloadExcel(`${exportBaseName}-registrations.xls`, registrationExportRows)}
                className="export-button"
              >
                <SafeIcon icon={FiFileText} /> XLS
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-white text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 border-b">
                <tr>
                  <th className="px-8 py-5">Player</th>
                  <th className="px-8 py-5">Contact</th>
                  <th className="px-8 py-5">Status</th>
                  <th className="px-8 py-5">Price</th>
                  <th className="px-8 py-5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loadingRegistrations ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="p-24 text-center text-gray-400 font-black uppercase tracking-[0.4em] animate-pulse"
                    >
                      Loading registrations...
                    </td>
                  </tr>
                ) : registrations.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="p-24 text-center text-gray-400 font-bold">
                      No registrations submitted for this event yet.
                    </td>
                  </tr>
                ) : (
                  registrations.map((registration) => {
                    const status = registration.status || registration.registration_status || 'pending';
                    const rowKey = pickRegistrationId(registration);
                    const emailKey = `confirmation-${rowKey}`;

                    return (
                      <tr key={rowKey} className="hover:bg-gray-50/50 transition-all group">
                        <td className="px-8 py-6">
                          <div className="font-black text-gray-900 text-lg tracking-tight leading-none">
                            {registration.first_name || '-'} {registration.last_name || ''}
                          </div>
                          <div className="text-[10px] text-indigo-600 font-black uppercase tracking-widest mt-2">
                            {registration.gender || '-'} • DOB:{' '}
                            {registration.date_of_birth ? formatDateEU(registration.date_of_birth) : registration.birth_year || '-'}
                          </div>
                          <div className="text-xs text-gray-400 font-medium mt-1">
                            {registration.current_club || registration.current_team || ''}
                          </div>
                        </td>
                        <td className="px-8 py-6">
                          <div className="text-sm font-bold text-gray-700">
                            {registration.email || '-'}
                          </div>
                          <div className="text-xs font-medium text-gray-400 mt-1">
                            {registration.parent_mobile || registration.phone || '-'}
                          </div>
                          <div className="text-xs font-medium text-gray-400 mt-1">
                            {registration.parent_name || ''}
                          </div>
                        </td>
                        <td className="px-8 py-6">
                          <span
                            className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${statusBadgeClasses(
                              status
                            )}`}
                          >
                            {status}
                          </span>
                        </td>
                        <td className="px-8 py-6">
                          <div className="font-black text-gray-900">
                            {formatMoney(registration.final_price)}
                          </div>
                          {registration.discount_code ? (
                            <div className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mt-1">
                              {registration.discount_code}
                            </div>
                          ) : null}
                        </td>
                        <td className="px-8 py-6 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => setViewingRegistration(registration)}
                              className="action-button"
                              title="View details"
                            >
                              <SafeIcon icon={FiEye} />
                            </button>
                            <button
                              type="button"
                              onClick={() => sendEmailToRow(registration, 'confirmation')}
                              disabled={emailBusyKey === emailKey}
                              className="action-button text-indigo-600 hover:bg-indigo-50"
                              title="Resend confirmation email"
                            >
                              <SafeIcon icon={emailBusyKey === emailKey ? FiRefreshCw : FiMail} className={emailBusyKey === emailKey ? 'animate-spin' : ''} />
                            </button>
                            <button
                              type="button"
                              onClick={() => openCustomEmailModal(registration)}
                              className="action-button text-purple-600 hover:bg-purple-50"
                              title="Send custom message"
                            >
                              <SafeIcon icon={FiSend} />
                            </button>
                            {status !== 'approved' ? (
                              <button
                                type="button"
                                onClick={() => handleRegistrationStatus(registration, 'approved')}
                                className="action-button text-emerald-600 hover:bg-emerald-50"
                                title="Approve"
                              >
                                <SafeIcon icon={FiCheckCircle} />
                              </button>
                            ) : null}
                            {status !== 'rejected' ? (
                              <button
                                type="button"
                                onClick={() => handleRegistrationStatus(registration, 'rejected')}
                                className="action-button text-red-600 hover:bg-red-50"
                                title="Reject"
                              >
                                <SafeIcon icon={FiX} />
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {activeTab === 'approved' ? (
        <div className="bg-white border rounded-[2.5rem] shadow-sm overflow-hidden">
          <div className="p-8 border-b bg-gray-50/50 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-xl font-black text-gray-900 tracking-tight">
                Approved Participants
              </h2>
              <p className="text-xs text-gray-500 font-bold uppercase tracking-widest mt-1">
                Approved players and payment tracking
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => sendBulkEmail(participants, 'approval')}
                disabled={emailBusyKey === 'bulk-approval'}
                className="export-button"
              >
                <SafeIcon icon={FiSend} />
                {emailBusyKey === 'bulk-approval' ? 'Sending...' : 'Email Approved'}
              </button>
              <button
                type="button"
                onClick={() => sendBulkEmail(participants, 'payment')}
                disabled={emailBusyKey === 'bulk-payment'}
                className="export-button"
              >
                <SafeIcon icon={FiCreditCard} />
                {emailBusyKey === 'bulk-payment' ? 'Sending...' : 'Email Payments'}
              </button>
              <button
                type="button"
                onClick={() => openBulkCustomEmailModal(participants)}
                className="export-button"
              >
                <SafeIcon icon={FiSend} />
                Custom Message
              </button>
              <button
                type="button"
                onClick={() => downloadCsv(`${exportBaseName}-approved-participants.csv`, participantExportRows)}
                className="export-button"
              >
                <SafeIcon icon={FiDownload} /> CSV
              </button>
              <button
                type="button"
                onClick={() => downloadExcel(`${exportBaseName}-approved-participants.xls`, participantExportRows)}
                className="export-button"
              >
                <SafeIcon icon={FiFileText} /> XLS
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-white text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 border-b">
                <tr>
                  <th className="px-8 py-5">Player</th>
                  <th className="px-8 py-5">Contact</th>
                  <th className="px-8 py-5">Payment</th>
                  <th className="px-8 py-5">Amount Due</th>
                  <th className="px-8 py-5">Amount Paid</th>
                  <th className="px-8 py-5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loadingParticipants ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="p-24 text-center text-gray-400 font-black uppercase tracking-[0.4em] animate-pulse"
                    >
                      Loading approved participants...
                    </td>
                  </tr>
                ) : participants.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="p-24 text-center text-gray-400 font-bold">
                      No approved participants found for this event.
                    </td>
                  </tr>
                ) : (
                  participants.map((participant) => {
                    const paymentStatus = participant.payment_status || 'pending';
                    const rowKey = pickParticipantId(participant);
                    const paymentEmailKey = `payment-${rowKey}`;
                    const approvalEmailKey = `approval-${rowKey}`;

                    return (
                      <tr key={rowKey} className="hover:bg-gray-50/50 transition-all group">
                        <td className="px-8 py-6">
                          <div className="font-black text-gray-900 text-lg tracking-tight leading-none">
                            {participant.first_name || '-'} {participant.last_name || ''}
                          </div>
                          <div className="text-[10px] text-indigo-600 font-black uppercase tracking-widest mt-2">
                            {participant.gender || '-'} • DOB:{' '}
                            {participant.date_of_birth ? formatDateEU(participant.date_of_birth) : participant.birth_year || '-'}
                          </div>
                          <div className="text-xs text-gray-400 font-medium mt-1">
                            {participant.current_club || participant.current_team || ''}
                          </div>
                        </td>
                        <td className="px-8 py-6">
                          <div className="text-sm font-bold text-gray-700">
                            {participant.email || '-'}
                          </div>
                          <div className="text-xs font-medium text-gray-400 mt-1">
                            {participant.parent_mobile || participant.phone || '-'}
                          </div>
                          <div className="text-xs font-medium text-gray-400 mt-1">
                            {participant.parent_name || ''}
                          </div>
                        </td>
                        <td className="px-8 py-6">
                          <span
                            className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${statusBadgeClasses(
                              paymentStatus
                            )}`}
                          >
                            {paymentStatus}
                          </span>
                          {participant.payment_gateway ? (
                            <div className="mt-1 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                              {participant.payment_gateway}
                            </div>
                          ) : null}
                        </td>
                        <td className="px-8 py-6 font-black text-gray-900">
                          {formatMoney(resolveAmountDue(participant, selectedGroup))}
                        </td>
                        <td className="px-8 py-6 font-black text-emerald-600">
                          {formatMoney(participant.amount_paid)}
                        </td>
                        <td className="px-8 py-6 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => sendEmailToRow(participant, 'approval')}
                              disabled={emailBusyKey === approvalEmailKey}
                              className="action-button text-emerald-600 hover:bg-emerald-50"
                              title="Send approval email"
                            >
                              <SafeIcon icon={emailBusyKey === approvalEmailKey ? FiRefreshCw : FiSend} className={emailBusyKey === approvalEmailKey ? 'animate-spin' : ''} />
                            </button>
                            <button
                              type="button"
                              onClick={() => sendEmailToRow(participant, 'payment')}
                              disabled={emailBusyKey === paymentEmailKey}
                              className="action-button text-indigo-600 hover:bg-indigo-50"
                              title="Send payment email"
                            >
                              <SafeIcon icon={emailBusyKey === paymentEmailKey ? FiRefreshCw : FiMail} className={emailBusyKey === paymentEmailKey ? 'animate-spin' : ''} />
                            </button>
                            <button
                              type="button"
                              onClick={() => openCustomEmailModal(participant)}
                              className="action-button text-purple-600 hover:bg-purple-50"
                              title="Send custom message"
                            >
                              <SafeIcon icon={FiSend} />
                            </button>
                            <button
                              type="button"
                              onClick={() => openPaymentModal(participant)}
                              className="w-11 h-11 inline-flex items-center justify-center text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-2xl shadow-sm transition-all border border-transparent hover:border-indigo-100"
                              title="Update payment"
                            >
                              <SafeIcon icon={FiCreditCard} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {editingPayment ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md p-4">
          <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="p-8 border-b bg-gray-50/50 flex items-center justify-between">
              <h2 className="text-xl font-black text-gray-900 tracking-tight">
                Record Payment
              </h2>
              <button type="button" onClick={() => setEditingPayment(null)}>
                <SafeIcon icon={FiX} />
              </button>
            </div>

            <form onSubmit={updatePayment} className="p-10 space-y-6">
              <div className="rounded-2xl bg-gray-50 border border-gray-100 p-5">
                <div className="text-lg font-black text-gray-900">
                  {editingPayment.first_name} {editingPayment.last_name}
                </div>
                <div className="text-xs font-bold text-gray-400 mt-1">
                  {editingPayment.email || editingPayment.phone || '-'}
                </div>
              </div>

              <div className="space-y-2">
                <label className="label-small">Payment Status</label>
                <select
                  value={editingPayment.payment_status}
                  onChange={(e) => setEditingPayment({ ...editingPayment, payment_status: e.target.value })}
                  className="field-input"
                >
                  {PAYMENT_STATUSES.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="label-small">Total Price (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingPayment.amount_due}
                    readOnly
                    className="field-input bg-gray-50 text-gray-500"
                  />
                </div>

                <div className="space-y-2">
                  <label className="label-small">Already Paid (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingPayment.amount_paid}
                    readOnly
                    className="field-input bg-gray-50 text-gray-500"
                  />
                </div>
              </div>

              <div className="rounded-2xl bg-amber-50 border border-amber-100 p-5">
                <div className="text-[10px] font-black text-amber-600 uppercase tracking-widest">
                  Remaining Balance After This Payment
                </div>
                <div className="text-2xl font-black text-amber-700 mt-1">
                  {formatMoney(
                    Math.max(
                      Number(editingPayment.remaining_amount || 0) -
                        Number(editingPayment.new_payment_amount || 0),
                      0
                    )
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <label className="label-small">New Payment Amount (€)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editingPayment.new_payment_amount}
                  onChange={(e) =>
                    setEditingPayment({
                      ...editingPayment,
                      new_payment_amount: e.target.value,
                    })
                  }
                  className="field-input"
                />
              </div>

              <div className="space-y-2">
                <label className="label-small">Payment Method</label>
                <select
                  value={editingPayment.payment_gateway || ''}
                  onChange={(e) => setEditingPayment({ ...editingPayment, payment_gateway: e.target.value })}
                  className="field-input"
                >
                  {PAYMENT_GATEWAYS.map((gateway) => (
                    <option key={gateway.value || 'none'} value={gateway.value}>
                      {gateway.label}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="submit"
                className="w-full py-5 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest shadow-xl shadow-indigo-100 flex items-center justify-center gap-3"
              >
                <SafeIcon icon={FiSave} />
                Update Payment Record
              </button>
            </form>
          </div>
        </div>
      ) : null}

      {viewingRegistration ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md p-4">
          <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-3xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="p-8 border-b bg-gray-50/50 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-black text-gray-900 tracking-tight">
                  Registration Details
                </h2>
                <p className="text-xs font-bold text-gray-400 mt-1">
                  {viewingRegistration.first_name} {viewingRegistration.last_name}
                </p>
              </div>
              <button type="button" onClick={() => setViewingRegistration(null)}>
                <SafeIcon icon={FiX} />
              </button>
            </div>

            <div className="p-8 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(viewingRegistration).map(([key, value]) => {
                  const isDateKey = key.includes('at') || key.includes('date') || key === 'date_of_birth';
                  let displayValue = value === null || value === undefined || value === '' ? '-' : String(value);

                  if (displayValue !== '-' && isDateKey) {
                    displayValue = key.includes('at') ? formatDateTimeEU(value) : formatDateEU(value);
                  }

                  return (
                    <div key={key} className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                      <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                        {key.replace(/_/g, ' ')}
                      </div>
                      <div className="mt-2 text-sm font-bold text-gray-800 break-words">
                        {typeof value === 'object' && value !== null ? JSON.stringify(value) : displayValue}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setViewingRegistration(null)}
                className="px-6 py-3 text-xs font-black text-gray-500 uppercase tracking-widest"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  handleRegistrationStatus(viewingRegistration, 'approved');
                  setViewingRegistration(null);
                }}
                className="px-6 py-3 rounded-2xl bg-emerald-600 text-white text-xs font-black uppercase tracking-widest"
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <EmailMessageModal
        open={emailMessageModal.open}
        onClose={() =>
          setEmailMessageModal({
            open: false,
            mode: 'single',
            participant: null,
            participants: [],
          })
        }
        token={session?.token || session?.session_token || session?.access_token || ''}
        tenant={modalTenant}
        intakeGroup={selectedGroup}
        participant={emailMessageModal.participant}
        participants={emailMessageModal.participants}
        mode={emailMessageModal.mode}
        onSent={(response) => {
          setSuccessMessage(
            emailMessageModal.mode === 'bulk'
              ? `Custom message completed. Sent: ${response?.sent || 0}. Failed: ${response?.failed || 0}.`
              : 'Custom message sent successfully'
          );
        }}
      />

      <style>{`
        .label-small { font-size: 10px; font-weight: 900; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.2em; }
        .field-input { width: 100%; padding: 0.85rem 1.25rem; border-radius: 1rem; border: 2px solid #f3f4f6; font-size: 0.875rem; font-weight: 700; background: #fff; outline: none; transition: border-color 0.2s; }
        .field-input:focus { border-color: #4f46e5; }
        .export-button { display: inline-flex; align-items: center; gap: 0.5rem; padding: 0.75rem 1rem; border-radius: 1rem; border: 2px solid #f3f4f6; background: white; color: #374151; font-size: 0.75rem; font-weight: 900; text-transform: uppercase; letter-spacing: 0.12em; transition: all 0.2s; }
        .export-button:hover { background: #f9fafb; border-color: #e5e7eb; }
        .export-button:disabled { opacity: 0.6; cursor: not-allowed; }
        .action-button { width: 2.75rem; height: 2.75rem; display: inline-flex; align-items: center; justify-content: center; border-radius: 1rem; color: #6b7280; background: #f3f4f6; font-weight: 900; transition: all 0.2s; }
        .action-button:hover { background: #e5e7eb; color: #111827; }
        .action-button:disabled { opacity: 0.6; cursor: not-allowed; }
      `}</style>
    </div>
  );
}